import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { map } from 'rxjs/operators';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

// Recurring contract charges per SYSTEM — rent, operating support, service and
// anything else billed month after month regardless of what was consumed.
//
// Modelled on the invoices Frigg already issues by hand in PowerOffice Go, e.g.
// invoice 13591 to Asker Kommune:
//     Leie friggkantine per måned           10 000,00   25%
//     Driftstøtte Friggkantine per måned     4 500,00   25%
//     Administrasjonsgebyr                       59,00   25%
// ...and 13618 to the same customer for their OTHER site, at a different rent.
// The amount is therefore per SYSTEM, never per customer.
//
// The 59 kr administrasjonsgebyr is NOT modelled here — it is charged
// automatically for every linked system by DriftkostnadService, which already
// does exactly that. This collection is for the negotiated, per-system amounts.
//
// All of these are ordinary 25%-VAT charges, not food: see VAT_BASIS_BY_KIND in
// billing.component.ts.

export const AVTALE_KINDS: { kind: string, label: string, defaultDescription: string }[] = [
  { kind: 'leie', label: 'Leie', defaultDescription: 'Leie friggkantine per måned' },
  { kind: 'driftstotte', label: 'Driftstøtte', defaultDescription: 'Driftstøtte Friggkantine per måned' },
  { kind: 'service', label: 'Service', defaultDescription: 'Serviceavtale per måned' },
  { kind: 'annet', label: 'Annet', defaultDescription: '' }
];

export interface Avtalelinje {
  dbid?: string;
  systemId: string;
  kind: string;
  description: string;
  amountExVat: number;
  interval: 'monthly' | 'quarterly' | 'yearly';
  startMonth: string;          // 'YYYY-MM', inclusive
  endMonth: string | null;     // 'YYYY-MM', inclusive; null = open-ended
  active: boolean;
}

export interface AvtaleBillable {
  total: number;
  lines: { systemId: string, systemName: string, kind: string, description: string, amountExVat: number }[];
}

@Injectable({ providedIn: 'root' })
export class AvtalelinjeService {

  constructor(private afs: AngularFirestore) { }

  list$() {
    return this.afs.collection('system-avtalelinjer').snapshotChanges().pipe(map(docs =>
      docs.map(d => ({ dbid: d.payload.doc.id, ...(d.payload.doc.data() as any) } as Avtalelinje))
    ));
  }

  add(line: Avtalelinje) {
    const { dbid, ...data } = line as any;
    return this.afs.collection('system-avtalelinjer').add({ ...data, createdAt: Date.now() });
  }

  update(dbid: string, patch: Partial<Avtalelinje>) {
    return this.afs.collection('system-avtalelinjer').doc(dbid).update({ ...patch, updatedAt: Date.now() });
  }

  remove(dbid: string) {
    return this.afs.collection('system-avtalelinjer').doc(dbid).delete();
  }

  // Does a line fall due in the given month? Unlike driftkostnad (which reflects
  // today's state whatever month you ask for), these are properly month-scoped:
  // start/end bound the agreement and `interval` decides which months inside it
  // are charged, counting from startMonth so a quarterly line lands on the same
  // months every year. Back-billing an old month is therefore correct.
  static dueInMonth(line: Avtalelinje, month: string): boolean {
    if (line.active === false) { return false; }
    if (!line.startMonth || line.startMonth > month) { return false; }
    if (line.endMonth && line.endMonth < month) { return false; }
    if (line.interval === 'monthly' || !line.interval) { return true; }
    const step = line.interval === 'quarterly' ? 3 : 12;
    const months = moment(month, 'YYYY-MM').diff(moment(line.startMonth, 'YYYY-MM'), 'months');
    return months >= 0 && months % step === 0;
  }

  // Per-customer charges for a month. The payer is the system's OWNER, resolved
  // through system-kunder exactly as DriftkostnadService and GarantiomsetningService
  // do — a system with no link is reported, never silently billed to someone else.
  async getBillableByCustomer(month: string): Promise<{ [kundekortId: string]: AvtaleBillable }> {
    const [lineSnap, sysSnap, linkSnap, kunderSnap] = await Promise.all([
      this.afs.collection('system-avtalelinjer').ref.get(),
      this.afs.collection('system_info').ref.get(),
      this.afs.collection('system-kunder').ref.get(),
      this.afs.collection('kunder').ref.get()
    ]);

    // A link whose kundekort was deleted is not an owner. Deleting a customer
    // leaves its system-kunder links behind, and because the map below is
    // last-write-wins, a dead link can overwrite a live one and make the system
    // resolve to a customer that no longer exists — so re-linking it by hand in
    // the admin panel would appear to work and change nothing.
    const liveCustomer = new Set(kunderSnap.docs.map(d => d.id));
    const linkBySystem: { [systemId: string]: string } = {};
    linkSnap.docs.forEach(d => {
      const l = d.data() as any;
      if (l.systemId && l.kundekortId && liveCustomer.has(l.kundekortId)) { linkBySystem[String(l.systemId)] = l.kundekortId; }
    });

    const sysName: { [id: string]: string } = {};
    const aliasToDocId: { [id: string]: string } = {};
    sysSnap.docs.forEach(d => {
      const s = d.data() as any;
      sysName[d.id] = s.name || d.id;
      if (s.systemId) { sysName[String(s.systemId)] = s.name || d.id; aliasToDocId[String(s.systemId)] = d.id; }
    });
    // system-kunder keys on either the doc id or the systemId field — the same
    // dual-spelling DriftkostnadService has to handle.
    const ownerOf = (systemId: string) =>
      linkBySystem[systemId] || linkBySystem[aliasToDocId[systemId]] || null;

    const out: { [kundekortId: string]: AvtaleBillable } = {};
    lineSnap.docs.forEach(d => {
      const line = { dbid: d.id, ...(d.data() as any) } as Avtalelinje;
      if (!AvtalelinjeService.dueInMonth(line, month)) { return; }
      const owner = ownerOf(String(line.systemId));
      if (!owner) { return; }
      const amount = Number(line.amountExVat) || 0;
      if (!(amount > 0)) { return; }
      const bucket = out[owner] || (out[owner] = { total: 0, lines: [] });
      bucket.lines.push({
        systemId: String(line.systemId),
        systemName: sysName[String(line.systemId)] || String(line.systemId),
        kind: line.kind,
        description: line.description || '',
        amountExVat: amount
      });
      bucket.total += amount;
    });
    Object.values(out).forEach(b => { b.total = +b.total.toFixed(2); });
    return out;
  }
}
